import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Container, Link, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Logo } from './Logo';
import { Wallet, BarChart3, Calendar, ChevronDown } from 'lucide-react';

function FeatureCard({ Icon, title, description }) {
  return (
    <Box sx={{ 
      bgcolor: 'background.paper',
      p: 4,
      borderRadius: 2,
      transition: 'transform 0.2s',
      '&:hover': {
        transform: 'translateY(-4px)'
      }
    }}>
      <Icon size={48} style={{ color: '#4e9b71', marginBottom: '1rem' }} />
      <Typography variant="h6" sx={{ color: 'common.white', mb: 1 }}>
        {title}
      </Typography>
      <Typography variant="body1" sx={{ color: 'text.secondary' }}>
        {description}
      </Typography>
    </Box>
  );
}

function PricingCard() {
  const navigate = useNavigate();

  return (
    <Box
      sx={{
        bgcolor: 'background.default',
        borderRadius: 2,
        p: 4,
        maxWidth: '400px',
        mx: 'auto',
        boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        border: '1px solid',
        borderColor: 'divider'
      }}
    >
      <Typography variant="h3" sx={{ textAlign: 'center', mb: 4, color: 'common.white' }}>
        FREE (During BETA)
      </Typography>
      <Box sx={{ mb: 4 }}>
        {[
          'Advanced profit tracking',
          'Detailed analytics',
          'Unlimited entries',
          'Bankroll management tools'
        ].map((feature) => (
          <Box key={feature} sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2 }}>
            <Box
              component="span"
              sx={{
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center'
              }}
            >
              ✓
            </Box>
            <Typography sx={{ color: 'common.white' }}>
              {feature}
            </Typography>
          </Box>
        ))}
      </Box>
      <Button
        variant="contained"
        fullWidth
        size="large"
        onClick={() => navigate('/register')}
        sx={{
          bgcolor: 'background.paper',
          color: 'common.white',
          py: 1.5,
          '&:hover': {
            bgcolor: 'primary.main'
          }
        }}
      >
        Get Started
      </Button>
    </Box>
  );
}

function FAQSection() {
  const faqs = [
    {
      question: "What is Pokerbankroll.me?",
      answer: "Pokerbankroll.me is a comprehensive poker tracking and analytics platform designed to help players monitor their performance, manage their bankroll, and improve their game through detailed statistics and insights."
    },
    {
      question: "How does Pokerbankroll.me help improve my poker game?",
      answer: "Our platform provides detailed analytics of your playing patterns, tracks your wins and losses, and offers insights into your performance across different game types and stakes. This data helps you identify strengths and areas for improvement in your game."
    },
    {
      question: "Can I use Pokerbankroll.me for different types of poker games?",
      answer: "Yes! Our platform supports tracking for all types of poker games including cash games, tournaments (both live and online), and sit & go's. You can categorize and analyze your results separately for each format."
    },
    {
      question: "Is my data secure with Pokerbankroll.me?",
      answer: "Absolutely. We use industry-standard encryption and security measures to protect your data. Your information is stored securely and is never shared with third parties."
    },
    {
      question: "Is this free to use?",
      answer: "Yes, Pokerbankroll.me is completely free to use during our beta period. You get access to all features without any restrictions."
    }
  ];

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto' }}>
      {faqs.map((faq, index) => (
        <Accordion
          key={index}
          sx={{
            bgcolor: 'background.default',
            color: 'common.white',
            mb: 1,
            '&:before': { display: 'none' },
            '& .MuiAccordionSummary-root': {
              borderBottom: '1px solid',
              borderColor: 'divider'
            }
          }}
        >
          <AccordionSummary
            expandIcon={<ChevronDown color="white" />}
            sx={{ 
              '&:hover': { bgcolor: 'background.paper' }
            }}
          >
            <Typography variant="h6">{faq.question}</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography sx={{ color: 'text.secondary' }}>
              {faq.answer}
            </Typography>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
}

function LandingPage() {
  const navigate = useNavigate();

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Navigation Bar */}
      <Box sx={{ 
        position: 'sticky', 
        top: 0, 
        bgcolor: 'background.default', 
        borderBottom: '1px solid',
        borderColor: 'divider',
        zIndex: 1000,
        py: 2
      }}>
        <Container maxWidth="lg">
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Logo className="h-8 w-8" />
              <Typography variant="h6" sx={{ color: 'common.white' }}>
                Stack Track
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 4 }}>
              <Link
                component="button"
                onClick={() => scrollToSection('features')}
                sx={{ 
                  color: 'common.white', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                Features
              </Link>
              <Link
                component="button"
                onClick={() => scrollToSection('pricing')}
                sx={{ 
                  color: 'common.white', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                Pricing
              </Link>
              <Link
                component="button"
                onClick={() => scrollToSection('faq')}
                sx={{ 
                  color: 'common.white', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                FAQ
              </Link>
              <Link
                component="button"
                onClick={() => scrollToSection('contact')}
                sx={{ 
                  color: 'common.white', 
                  textDecoration: 'none',
                  cursor: 'pointer',
                  '&:hover': { color: 'primary.main' }
                }}
              >
                Contact
              </Link>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Logo Section */}
      <Box 
        sx={{ 
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          pt: 6,
          mb: 8
        }}
      >
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: 3 
          }}
        >
          <Logo className="h-16 w-16" />
          <Typography 
            variant="h3" 
            sx={{ 
              color: 'common.white',
              fontWeight: 500,
              fontFamily: 'Anton, sans-serif',
              letterSpacing: '0.02em'
            }}
          >
            Stack Track
          </Typography>
        </Box>
      </Box>

      {/* Hero Section */}
      <Container maxWidth="lg" sx={{ textAlign: 'center', pt: 8 }}>
        <Typography 
          variant="h2" 
          component="h1" 
          sx={{ 
            color: 'common.white',
            mb: 3,
            fontWeight: 'bold',
            fontSize: { xs: '2.5rem', md: '3.75rem' }
          }}
        >
          Track Your Poker Journey<br />Like Never Before
        </Typography>
        <Typography 
          variant="h5" 
          sx={{ 
            color: 'text.secondary',
            mb: 6,
            maxWidth: '800px',
            mx: 'auto'
          }}
        >
          Take control of your poker career with detailed bankroll tracking, advanced statistics, 
          and comprehensive game analysis all in one place.
        </Typography>
        <Box sx={{ mb: 8 }}>
          <Button 
            variant="contained" 
            size="large"
            sx={{ mr: 2 }}
            onClick={() => navigate('/register')}
          >
            Get Started Free
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            onClick={() => navigate('/login')}
          >
            Sign In
          </Button>
        </Box>
      </Container>

      {/* Features Section */}
      <Box id="features" sx={{ py: 12 }}>
        <Typography 
          variant="h3" 
          align="center" 
          sx={{ 
            color: 'common.white',
            mb: 8,
            fontWeight: 'bold'
          }}
        >
          Features
        </Typography>
        <Box sx={{ 
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
          gap: 4
        }}>
          <FeatureCard 
            Icon={Wallet}
            title="Bankroll Management"
            description="Track deposits, withdrawals, and session results with precision. Get insights into your bankroll growth over time."
          />
          <FeatureCard 
            Icon={BarChart3}
            title="Advanced Analytics"
            description="Dive deep into your performance with detailed statistics, win rates, and variance analysis."
          />
          <FeatureCard 
            Icon={Calendar}
            title="Live Poker Calendar"
            description="Stay updated with local tournaments, cash games, and special events. Never miss a profitable opportunity."
          />
        </Box>
      </Box>

      {/* Pricing Section */}
      <Box id="pricing" sx={{ py: 12, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 6, color: 'common.white' }}>
            Pricing
          </Typography>
          <Typography 
            variant="h6" 
            sx={{ 
              textAlign: 'center', 
              mb: 8, 
              color: 'text.secondary',
              maxWidth: '600px',
              mx: 'auto'
            }}
          >
            Start tracking your poker journey today with our free plan. No credit card required.
          </Typography>
          <PricingCard />
        </Container>
      </Box>

      {/* FAQ Section */}
      <Box id="faq" sx={{ py: 12 }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 6, color: 'common.white' }}>
            Frequently Asked Questions
          </Typography>
          <FAQSection />
        </Container>
      </Box>

      {/* Contact Section */}
      <Box id="contact" sx={{ py: 12, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Typography variant="h3" sx={{ textAlign: 'center', mb: 6, color: 'common.white' }}>
            Get in Touch
          </Typography>
          {/* Add your contact content here */}
        </Container>
      </Box>

      {/* CTA Section */}
      <Container sx={{ py: 12 }}>
        <Box sx={{ 
          bgcolor: 'primary.main',
          borderRadius: 4,
          p: 8,
          textAlign: 'center'
        }}>
          <Typography variant="h3" sx={{ color: 'common.white', mb: 2 }}>
            Ready to Level Up Your Poker Game?
          </Typography>
          <Typography variant="h6" sx={{ color: 'primary.contrastText', mb: 4 }}>
            Join thousands of players who are already tracking their way to success.
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: 'common.white', 
              color: 'primary.main',
              '&:hover': {
                bgcolor: 'grey.100'
              }
            }}
            onClick={() => navigate('/register')}
          >
            Get Started Now
          </Button>
        </Box>
      </Container>

      {/* Footer */}
      <Container>
        <Box sx={{ 
          py: 4, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Logo className="h-6 w-6" />
            <Typography sx={{ 
              color: 'common.white',
              fontFamily: 'Anton, sans-serif',
              letterSpacing: '0.02em'
            }}>
              Stack Track
            </Typography>
          </Box>
          <Typography sx={{ color: 'text.secondary' }}>
            © 2024 Stack Track. All rights reserved.
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}

export default LandingPage; 